import {
  NotificationActionType,
  Platform,
  TelegramCouplingType,
} from "@shared/features/settings/types";
import {
  GetNotificationsDataResponse,
  NotificationActionTypeData,
} from "@shared/types";
import { PLATFORM_TO_ACTIVE_KEY } from "@shared/features/settings/constants";
import {
  getNotificationSettingsSections,
  getSectionItem,
} from "@shared/features/settings/utils";
import { useMemo } from "react";

interface useNotificationsSettingsSectionProps {
  index: number;
  section: string;
  actionTypes: NotificationActionTypeData[];
  expanded?: Record<string, boolean>;
  notificationsData?: GetNotificationsDataResponse;
  onCheckboxClick: (
    actionTypes: NotificationActionType | NotificationActionType[],
    platform: Platform,
    isActive: boolean
  ) => void;
}

const useNotificationsSettingsSection = ({
  index,
  section,
  actionTypes,
  expanded,
  notificationsData,
  onCheckboxClick,
}: useNotificationsSettingsSectionProps) => {
  const isExpanded = expanded?.[section] ?? false;
  const sections = getNotificationSettingsSections(notificationsData) || [];
  const isLastSection = index === sections?.length - 1;
  const sectionItem = useMemo(
    () => getSectionItem(notificationsData, section),
    [notificationsData, section]
  );

  const getCheckboxData = (platform: Platform) => {
    const checkedArray =
      sectionItem?.data
        .map((actionType) => actionType[PLATFORM_TO_ACTIVE_KEY[platform]])
        .filter((checked: boolean) => checked !== null) || [];
    const numActions = checkedArray.length;
    const numActiveActions = checkedArray.filter((checked) => checked).length;
    const isChecked = numActiveActions > 0;
    const checkAll = numActiveActions === numActions;
    const indeterminate = isChecked && numActiveActions < numActions;
    const onClick = () =>
      onCheckboxClick(
        actionTypes.map((actionType) => actionType.type),
        platform,
        !isChecked
      );
    const hideCheckbox = numActions <= 0;

    return {
      numActiveActions,
      isChecked,
      checkAll,
      indeterminate,
      onClick,
      hideCheckbox,
    };
  };

  const telegramCheckboxData = getCheckboxData(
    TelegramCouplingType.TELEGRAM_BOT
  );
  const webCheckboxData = getCheckboxData(TelegramCouplingType.WEB_OR_APP);

  const telegramToggleOn = notificationsData?.telegram_notifications_active;
  const webToggleOn = notificationsData?.web_notifications_active;

  return {
    isExpanded,
    telegramCheckboxData,
    webCheckboxData,
    isLastSection,
    telegramToggleOn,
    webToggleOn,
  };
};

export default useNotificationsSettingsSection;
